import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AuthService } from 'app/common/services/auth';
import { User } from 'app/common/models/user';

@Injectable({
  providedIn: 'root'
})
export class CoreMenuService {
  private _router = inject(Router);
  private authService = inject(AuthService);

  currentUser: User;

  onItemCollapsed: Subject<any>;
  onItemCollapseToggled: Subject<any>;

  // Subjects
  private _onMenuRegistered: BehaviorSubject<any>;
  private _onMenuUnregistered: BehaviorSubject<any>;
  private _onMenuChanged: BehaviorSubject<any>;

  // Current menu
  private _currentMenuKey: string;

  // Filtered menu registry
  private _registry: { [key: string]: any } = {};

  // ORIGINAL menus - important!
  private _originalRegistry: { [key: string]: any } = {};

  constructor() {
    this.onItemCollapsed = new Subject();
    this.onItemCollapseToggled = new Subject();

    this._currentMenuKey = null;

    this._onMenuRegistered = new BehaviorSubject(null);
    this._onMenuUnregistered = new BehaviorSubject(null);
    this._onMenuChanged = new BehaviorSubject(null);

    /**
     * Watch logged-in user
     */
    this.authService.currentUser.subscribe(user => {
      this.currentUser = user;

      console.log('================================');
      console.log('CURRENT USER:', this.currentUser);
      console.log('CURRENT USER ROLES:', this.currentUser?.roles);
      console.log('================================');

      /**
       * Rebuild menus from ORIGINAL menus
       * whenever the user becomes available/changes.
       */
      this.refreshMenus();
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // Accessors
  // -----------------------------------------------------------------------------------------------------

  get onMenuRegistered(): Observable<any> {
    return this._onMenuRegistered.asObservable();
  }

  get onMenuUnregistered(): Observable<any> {
    return this._onMenuUnregistered.asObservable();
  }

  get onMenuChanged(): Observable<any> {
    return this._onMenuChanged.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // Menu registration
  // -----------------------------------------------------------------------------------------------------

  register(key: string, menu: any[]): void {
    if (this._registry[key]) {
      console.error(
        `Menu with the key '${key}' already exists. Either unregister it first or use a unique key.`
      );

      return;
    }

    /**
     * IMPORTANT:
     * Keep the ORIGINAL menu.
     *
     * Do not overwrite this with the filtered version.
     */
    this._originalRegistry[key] = menu;

    /**
     * Filter the original menu according to current user.
     */
    const filteredMenu = this.filterMenuByRole(menu);

    this._registry[key] = filteredMenu;

    console.log('================================');
    console.log('MENU REGISTERED:', key);
    console.log('ORIGINAL MENU:', menu);
    console.log('FILTERED MENU:', filteredMenu);
    console.log('================================');

    this._onMenuRegistered.next([key, filteredMenu]);
  }

  // -----------------------------------------------------------------------------------------------------
  // Unregister
  // -----------------------------------------------------------------------------------------------------

  unregister(key: string): void {
    if (!this._registry[key]) {
      console.warn(
        `Menu with the key '${key}' doesn't exist in the registry.`
      );
    }

    delete this._registry[key];

    // Also remove original menu
    delete this._originalRegistry[key];

    this._onMenuUnregistered.next(key);
  }

  // -----------------------------------------------------------------------------------------------------
  // Get menu
  // -----------------------------------------------------------------------------------------------------

  getMenu(key: string): any {
    if (!this._registry[key]) {
      console.warn(
        `Menu with the key '${key}' doesn't exist in the registry.`
      );

      return;
    }

    return this._registry[key];
  }

  // -----------------------------------------------------------------------------------------------------
  // Get current menu
  // -----------------------------------------------------------------------------------------------------

  getCurrentMenu(): any {
    if (!this._currentMenuKey) {
      console.warn(`The current menu is not set.`);

      return;
    }

    return this.getMenu(this._currentMenuKey);
  }

  // -----------------------------------------------------------------------------------------------------
  // Set current menu
  // -----------------------------------------------------------------------------------------------------

  setCurrentMenu(key: string): void {
    if (!this._registry[key]) {
      console.warn(
        `Menu with the key '${key}' doesn't exist in the registry.`
      );

      return;
    }

    this._currentMenuKey = key;

    this._onMenuChanged.next(key);
  }

  // -----------------------------------------------------------------------------------------------------
  // Get user roles
  // -----------------------------------------------------------------------------------------------------

  private getUserRoles(): string[] {
    const roles = this.currentUser?.roles;

    console.log('ROLES USED FOR MENU:', roles);

    if (!roles) {
      return [];
    }

    if (Array.isArray(roles)) {
      return roles;
    }

    if (typeof roles === 'string') {
      return [roles];
    }

    return [];
  }

  // -----------------------------------------------------------------------------------------------------
  // Check menu permission
  // -----------------------------------------------------------------------------------------------------

  private hasRequiredRole(item: any): boolean {
    /**
     * No role means everyone can see it.
     */
    if (!item.role || item.role.length === 0) {
      return true;
    }

    const userRoles = this.getUserRoles();
    if (!userRoles.length) {
      return false;
    }

    const priorityMap: Record<string, number> = {
      IdentitySuperAdmin: 3,
      IdentityAdmin: 2,
      IdentityModerator: 1
    };

    const highestUserPriority = Math.max(...userRoles.map((role) => priorityMap[role] ?? 0));
    const itemHighestPriority = Math.max(...item.role.map((requiredRole: string) => priorityMap[requiredRole] ?? 0));

    /**
     * If a user has a higher role assigned, hide lower-priority menu items.
     * Example: IdentityAdmin should not see HR dashboard while also holding IdentityModerator.
     */
    if (highestUserPriority > 0 && itemHighestPriority < highestUserPriority) {
      return false;
    }

    /**
     * User needs at least one matching role.
     */
    return item.role.some((requiredRole: string) =>
      userRoles.includes(requiredRole)
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // Filter menu
  // -----------------------------------------------------------------------------------------------------

  private filterMenuByRole(menu: any[]): any[] {
    if (!menu || !Array.isArray(menu)) {
      return [];
    }

    return menu
      .filter(item => {
        return this.hasRequiredRole(item);
      })
      .map(item => {
        /**
         * Filter children recursively.
         */
        if (item.children && Array.isArray(item.children)) {
          return {
            ...item,
            children: this.filterMenuByRole(item.children)
          };
        }

        return item;
      })
      .filter(item => {
        /**
         * If a collapsible menu has no visible children,
         * remove the collapsible parent.
         */
        if (item.type === 'collapsible') {
          return item.children && item.children.length > 0;
        }

        return true;
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // Refresh menus
  // -----------------------------------------------------------------------------------------------------

  private refreshMenus(): void {
    /**
     * If user hasn't loaded yet, don't destroy the menu.
     */
    if (!this.currentUser) {
      console.log('Menu refresh skipped: current user not loaded yet.');
      return;
    }

    console.log('================================');
    console.log('REFRESHING MENUS');
    console.log('USER:', this.currentUser);
    console.log('ROLES:', this.currentUser?.roles);
    console.log('================================');

    Object.keys(this._originalRegistry).forEach(key => {
      /**
       * VERY IMPORTANT:
       *
       * Always filter the ORIGINAL menu.
       *
       * Never filter the already-filtered menu.
       */
      const originalMenu = this._originalRegistry[key];

      const filteredMenu = this.filterMenuByRole(originalMenu);

      this._registry[key] = filteredMenu;

      console.log('================================');
      console.log('MENU:', key);
      console.log('FILTERED:', filteredMenu);
      console.log('================================');

      /**
       * Notify sidebar that menu changed.
       */
      this._onMenuChanged.next(key);
    });
  }
}